import { Injectable } from '@angular/core';
import { ApgPortfolio, makeSeedDefaultPortfolio, sortPortfolios } from '../model/ApgPortfolio';
import { SettingsStorageService } from 'projects/shared-components/settings-storage-service.service';
import { ServiceConfiguration } from './ServiceConfiguration';
import { isVoid } from 'projects/shared-components/utils';
import { SessionService } from 'projects/shared-components/authentication/session-service.service';
import { ApplicationSettingsService } from 'projects/shared-components/app-settings/application-settings.service';

const StorageKey = 'apg.portfolios';
const ComparisonStorageKeyLeft = 'cpg.portfolios.left';
const ComparisonStorageKeyRight = 'cpg.portfolios.right';

// CR-20260928-001: the portfolio the OWN user last selected in the APG, restored at boot (ruling
// Q1: last used, the default portfolio as the fallback). Per user; holds `{userId, id}`.
const LastSelectedKey = 'apg.last-selected-portfolio';

export interface LastSelectedPortfolio {
   userId: string;
   id: string;
}

@Injectable()
export class CashFlowPortfoliosService {
   constructor(
      private _settingsStorage: SettingsStorageService,
      private _sessionService: SessionService,
      private _applicationSettings: ApplicationSettingsService
   ) {

   }

   //
   private _serviceConfig: ServiceConfiguration;

   //
   configure(cfg: ServiceConfiguration) {
      this._serviceConfig = cfg;
   }

   //
   getPortfolios(): ApgPortfolio[] {

      const key = this.makeStorageKey();

      let portfolios = this._settingsStorage
          .getItem<ApgPortfolio[]>(key, this._serviceConfig?.userId) || [];

      // The retired '----' scratchpad may still sit in a list the migration has not rewritten
      // (or a list an old bundle saved after it). It is never a portfolio any more: drop it.
      portfolios = portfolios.filter(x => !isVoid(x) && x.id !== '----');

      // Ruling Q8: a user with NO portfolio gets one real "Default Portfolio" the first time his
      // OWN main list is read empty. Never seeded for another user (a super user reading a
      // colleague's list) and never for the Package Comparison side lists.
      if (portfolios.length === 0 && this.isOwnMainList()) {
         const apgSettings = this._applicationSettings.adjustmentPricingGrid;
         const seed = makeSeedDefaultPortfolio(
            this._serviceConfig.userId,
            this._serviceConfig.userName,
            (apgSettings && apgSettings.defaultUnderlying) || 'SPX',
            (apgSettings && apgSettings.defaultStrategy) || 'Calls & Puts'
         );
         portfolios = [seed];
         this.savePortfolios(portfolios);
      }

      // Owner ruling (smoke 2026-09-28): EXACTLY one default, always. A list with none (older data,
      // or a flag lost somewhere) gets its first portfolio by name flagged, and the own main list
      // is saved so the state never stays ambiguous.
      if (portfolios.length > 0 && !portfolios.some(x => x.isDefault)) {
         const first = sortPortfolios(portfolios)[0];
         first.isDefault = true;
         if (this.isOwnMainList()) {
            this.savePortfolios(portfolios);
         }
      }

      // The OWNER comes from the reading context, never from the stored row: the migration wrote
      // entries with no user name (first smoke 2026-09-28: the selector read "null | Default
      // Portfolio"), and a stored name goes stale when a user is renamed anyway.
      portfolios.forEach(x => {
         x.userId = x.userId || this._serviceConfig.userId;
         x.userName = this._serviceConfig.userName || x.userName;
      });

      return sortPortfolios(portfolios);
   }

   //
   savePortfolios(portfolios: ApgPortfolio[]) {
      const key = this.makeStorageKey();
      this._settingsStorage.setItem(key, this.enforceSingleDefault(portfolios), this._serviceConfig?.userId);
   }

   /**
    * Mark `portfolio` as the user's default and clear the flag on every other portfolio of the
    * list (the one-default invariant). Mutates the list in place; the caller saves.
    */
   setDefault(portfolios: ApgPortfolio[], portfolio: ApgPortfolio): void {
      (portfolios || []).forEach(x => {
         x.isDefault = (x === portfolio) || (x.id === portfolio.id);
      });
   }

   //
   getLastSelected(): LastSelectedPortfolio | undefined {
      const ownUserId = this._sessionService.sessionData?.userId;
      return this._settingsStorage.getItem<LastSelectedPortfolio>(LastSelectedKey, ownUserId) || undefined;
   }

   //
   saveLastSelected(portfolio: ApgPortfolio): void {
      if (isVoid(portfolio) || isVoid(portfolio.id)) {
         return;
      }
      const ownUserId = this._sessionService.sessionData?.userId;
      this._settingsStorage.setItem(LastSelectedKey, {userId: portfolio.userId, id: portfolio.id}, ownUserId);
   }

   // Exactly one flagged portfolio survives a save: the first flagged one wins; with none flagged,
   // the first by name is flagged.
   private enforceSingleDefault(portfolios: ApgPortfolio[]): ApgPortfolio[] {
      const list = portfolios || [];
      if (list.length > 0 && !list.some(x => !!x && x.isDefault)) {
         sortPortfolios(list)[0].isDefault = true;
      }
      let seen = false;
      list.forEach(x => {
         if (!x) {
            return;
         }
         if (x.isDefault) {
            if (seen) {
               x.isDefault = false;
            }
            seen = true;
         }
      });
      return list;
   }

   private isOwnMainList(): boolean {
      if (isVoid(this._serviceConfig) || !isVoid(this._serviceConfig.orientation)) {
         return false;
      }
      const ownUserId = this._sessionService.sessionData?.userId;
      return !isVoid(ownUserId) && this._serviceConfig.userId === ownUserId;
   }

   //
   private makeStorageKey(): string {

      console.assert(!isVoid(this._serviceConfig));

      let rootKey = StorageKey;

      if (this._serviceConfig.orientation === 'left') {
         rootKey = ComparisonStorageKeyLeft;
      } else if (this._serviceConfig.orientation === 'right') {
         rootKey = ComparisonStorageKeyRight;
      }

      return rootKey;
   }
}
